import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Global-aware Postal / ZIP field.
 * - India (default / country IN): keeps 6-digit PIN auto-fill from pincode_master.
 * - Other countries: accepts the country's real format (letters, digits, spaces,
 *   hyphens), validates + normalises via /api/address/validate, and never strips
 *   the value down to 6 numeric digits.
 */
export function PincodeField({ value, onChange, onResolve, onLocality, country = "IN", testid = "pincode" }) {
  const iso = String(country || "IN").trim().toUpperCase();
  const isIndia = iso === "IN" || iso === "INDIA" || iso === "";
  const [status, setStatus] = useState("idle"); // idle | loading | found | notfound | valid | invalid
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    const raw = String(value || "").trim();
    if (timer.current) clearTimeout(timer.current);

    if (isIndia) {
      const pin = raw.replace(/\D/g, "");
      if (pin.length !== 6) { setStatus("idle"); setResult(null); return; }
      timer.current = setTimeout(async () => {
        setStatus("loading");
        try {
          const { data } = await api.get(`/master/pincode/${pin}`);
          if (data.found) { setStatus("found"); setResult(data); onResolve?.(data); }
          else { setStatus("notfound"); setResult(null); }
        } catch { setStatus("notfound"); }
      }, 350);
      return () => timer.current && clearTimeout(timer.current);
    }

    // Global: country-aware validation/normalisation.
    if (!raw) { setStatus("idle"); setMsg(null); return; }
    timer.current = setTimeout(async () => {
      setStatus("loading");
      try {
        const { data } = await api.post(`/address/validate`, { country: iso, postal: raw });
        if (data.status === "VALID") {
          setStatus("valid"); setMsg(null);
          if (data.normalized && data.normalized !== raw) onChange(data.normalized);
        } else if (data.status === "OK") { setStatus("valid"); setMsg(data.message || null); }
        else { setStatus("invalid"); setMsg(data.message || `Expected format: ${data.format || "—"}`); }
      } catch { setStatus("idle"); setMsg(null); }
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, iso]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const v = e.target.value;
    if (isIndia) onChange(v.replace(/\D/g, "").slice(0, 6));
    else onChange(v.replace(/[^A-Za-z0-9 -]/g, "").toUpperCase().slice(0, 12));
  };

  return (
    <div>
      <Label className="flex items-center gap-1">
        <MapPin className="h-3 w-3 text-slate-400" /> {isIndia ? "PIN code" : "Postal / ZIP code"}
      </Label>
      <div className="relative">
        <Input value={value || ""} maxLength={isIndia ? 6 : 12} inputMode={isIndia ? "numeric" : "text"}
          onChange={handleChange}
          placeholder={isIndia ? "600010" : "e.g. 150-6018"} data-testid={`${testid}-input`} />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          {status === "loading" && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {(status === "found" || status === "valid") && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {(status === "notfound" || status === "invalid") && <AlertCircle className="h-4 w-4 text-amber-500" />}
        </span>
      </div>
      {status === "found" && result && (
        <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`${testid}-resolved`}>
          <span className="loc-value">{result.district}</span>, <span className="loc-value">{result.state}</span>
        </div>
      )}
      {status === "notfound" && (
        <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`${testid}-notfound`}>PIN not found — enter city/state manually.</div>
      )}
      {status === "invalid" && msg && (
        <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`${testid}-invalid`}>{msg}</div>
      )}
      {status === "found" && result?.localities?.length > 1 && (
        <select
          className="w-full h-8 mt-1 border border-slate-200 rounded-md px-2 text-[12px] bg-white"
          data-testid={`${testid}-locality`}
          defaultValue=""
          onChange={(e) => e.target.value && onLocality?.(e.target.value)}>
          <option value="">Pick locality / area (optional)…</option>
          {result.localities.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      )}
    </div>
  );
}

export default PincodeField;
